<template>
  <div class="game-slot-combinations-editor">
    <label v-if="configs.label" class="block mb-2">{{ t(configs.label) }}</label>

    <div class="combinations-list">
      <div
        v-for="(combo, index) in combinations"
        :key="index"
        class="combination-item"
      >
        <div class="combination-item-header">
          <i class="fas fa-grip-vertical drag-handle" />
          <span class="combination-number">{{ index + 1 }}</span>
        </div>

        <div class="combination-fields">
          <div class="field-row">
            <div class="field-group field-symbols">
              <label>{{ t('templateBuilder.games.winningSymbols') }}</label>
              <div class="symbols-selector">
                <Dropdown
                  v-for="(sym, symIndex) in combo.symbols"
                  :key="symIndex"
                  v-model="combo.symbols[symIndex]"
                  :options="availableSymbols"
                  option-label="label"
                  option-value="id"
                  class="symbol-dropdown"
                  panel-class="slot-symbol-dropdown-panel"
                  @change="onCombinationChange"
                >
                  <template #value="slotProps">
                    <span v-if="slotProps.value && isImageSymbol(slotProps.value)" class="symbol-preview symbol-preview-img">
                      <img :src="getSymbolImage(slotProps.value)" class="symbol-thumb" />
                    </span>
                    <span v-else-if="slotProps.value" class="symbol-preview">
                      {{ getSymbolDisplay(slotProps.value) }}
                    </span>
                  </template>
                  <template #option="slotProps">
                    <span class="symbol-option">
                      <img v-if="slotProps.option.type === 'image'" :src="slotProps.option.image" class="symbol-thumb" />
                      <span v-else class="symbol-option-icon">{{ slotProps.option.text || slotProps.option.image }}</span>
                    </span>
                  </template>
                </Dropdown>
              </div>
            </div>
            <div class="field-group field-prize">
              <label>{{ t('templateBuilder.games.prize') }}</label>
              <InputText
                v-model="combo.prize"
                class="w-full"
                @update:model-value="onCombinationChange"
              />
            </div>
            <div class="field-group field-probability">
              <label>{{ t('templateBuilder.games.probability') }}</label>
              <div class="probability-input">
                <InputNumber
                  v-model="combo.probability"
                  :min="0"
                  :max="100"
                  class="w-full"
                  @update:model-value="onCombinationChange"
                />
                <span class="probability-suffix">%</span>
              </div>
            </div>
            <div class="field-actions">
              <Button
                icon="far fa-trash-alt"
                class="p-button-text p-button-danger p-button-sm"
                :disabled="combinations.length <= 1"
                @click="removeCombination(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="combinations-footer">
      <div class="probability-total" :class="{ 'is-valid': totalProbability <= 100, 'is-warning': totalProbability > 100 }">
        <span>{{ t('templateBuilder.games.winProbability') }}</span>
        <span class="total-value">{{ totalProbability }} %</span>
      </div>
      <Button
        :label="t('templateBuilder.games.addCombination')"
        icon="far fa-plus"
        class="p-button-secondary p-button-sm"
        @click="addCombination"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';
import { syncGameFunnelSteps } from '@/composables/template-editor/TemplateEditor';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

interface SlotSymbol {
  id: string;
  type: string;
  image: string;
  text?: string;
  label: string;
}

interface WinCombination {
  symbols: string[];
  prize: string;
  probability: number;
  stock?: number | null;
}

export default defineComponent({
  name: 'GameSlotCombinationsEditor',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const combinations = ref<WinCombination[]>([]);
    const availableSymbols = ref<SlotSymbol[]>([]);

    const totalProbability = computed(() => {
      return combinations.value.reduce((sum, combo) => sum + (combo.probability || 0), 0);
    });

    const parseSymbolsFromElement = () => {
      try {
        // Read data-symbols from the actual DOM element in the iframe
        const iframeDoc = getTemplateIframeDocument();
        if (iframeDoc) {
          const slotEl = iframeDoc.querySelector('.spm_widget_game_slot_machine');
          if (slotEl) {
            const raw = slotEl.getAttribute('data-symbols');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed) && parsed.length > 0) {
                availableSymbols.value = parsed.map((s: any) => ({
                  id: s.id || '',
                  type: s.type || 'emoji',
                  image: s.image || '',
                  text: s.text || '',
                  label: s.label || s.image || s.id || '',
                }));
                return;
              }
            }
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }

      // Default symbols
      availableSymbols.value = [
        { id: 'cherry', type: 'emoji', image: '🍒', label: 'Cerise' },
        { id: 'lemon', type: 'emoji', image: '🍋', label: 'Citron' },
        { id: 'orange', type: 'emoji', image: '🍊', label: 'Orange' },
        { id: 'seven', type: 'emoji', image: '7️⃣', label: 'Sept' },
      ];
    };

    const getSymbolDisplay = (symbolId: string): string => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      if (!symbol) return '❓';
      if (symbol.type === 'image') return '';
      return symbol.text || symbol.image || '❓';
    };

    const isImageSymbol = (symbolId: string): boolean => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      return symbol?.type === 'image' || (!!(symbol?.image) && /^(https?:\/\/|\/)/.test(symbol?.image || ''));
    };

    const getSymbolImage = (symbolId: string): string => {
      const symbol = availableSymbols.value.find(s => s.id === symbolId);
      return symbol?.image || '';
    };

    /**
     * Stock limits are edited in the separate "Stock" accordion (GameStockEditor), which writes
     * them straight into the iframe attribute. This editor keeps its own copy of the combinations
     * and rewrites the whole attribute on every change, so the stock of every combination is
     * re-read from the iframe (source of truth) before each write (SC-3828).
     */
    const syncStockFromIframe = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;
        const element = iframeDoc.querySelector(props.parserValues.selector);
        if (!element) return;
        const iframeCombinations = JSON.parse(element.getAttribute('data-win-combinations') || '[]');
        if (!Array.isArray(iframeCombinations)) return;
        combinations.value.forEach((_combo: WinCombination, index: number) => {
          const stock = Number(iframeCombinations[index]?.stock);
          combinations.value[index].stock = stock > 0 ? stock : null;
        });
      } catch (e) {
        // Unreadable attribute: keep the local copy
      }
    };

    const parseInitialValue = () => {
      parseSymbolsFromElement();

      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            combinations.value = parsed.map((item: any) => ({
              symbols: item.symbols || ['cherry', 'cherry', 'cherry'],
              prize: item.prize || '',
              probability: item.probability || 0,
              stock: (item.stock != null && Number(item.stock) > 0) ? Number(item.stock) : null,
            }));
            syncStockFromIframe();
            return;
          }
        }
      } catch (e) {
        console.warn('[GameSlotCombinationsEditor] Failed to parse initial value:', e);
      }

      // Default combinations if parsing failed
      combinations.value = [
        { symbols: ['seven', 'seven', 'seven'], prize: '50% de réduction', probability: 2 },
        { symbols: ['cherry', 'cherry', 'cherry'], prize: '20% de réduction', probability: 10 },
      ];
    };

    const emitChange = () => {
      const jsonValue = JSON.stringify(combinations.value);
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
      // The field setter only applies this value ~50 ms later: write it right away (same JSON) so the
      // iframe is already up to date when the next change re-reads the stock by position (SC-3828)
      try {
        const element = getTemplateIframeDocument()?.querySelector(props.parserValues.selector);
        if (element) {
          element.setAttribute('data-win-combinations', jsonValue);
        }
      } catch (e) {
        // Iframe not available: the field setter will apply the value
      }
    };

    const onCombinationChange = () => {
      syncStockFromIframe();
      emitChange();
      syncGameFunnelSteps();
    };

    const addCombination = () => {
      syncStockFromIframe();
      const defaultSymbol = availableSymbols.value[0]?.id || 'cherry';

      combinations.value.push({
        symbols: [defaultSymbol, defaultSymbol, defaultSymbol],
        prize: 'Nouveau prix',
        probability: 5,
      });
      emitChange();
      syncGameFunnelSteps();
    };

    const removeCombination = (index: number) => {
      if (combinations.value.length > 1) {
        // Read the stocks before the splice so each stock follows its combination, not its position
        syncStockFromIframe();
        combinations.value.splice(index, 1);
        emitChange();
        syncGameFunnelSteps();
      }
    };

    // Watch for data-symbols changes in iframe via MutationObserver
    let symbolsObserver: MutationObserver | null = null;

    const startObservingSymbols = () => {
      try {
        const iframeDoc = getTemplateIframeDocument();
        if (!iframeDoc) return;
        const slotEl = iframeDoc.querySelector('.spm_widget_game_slot_machine');
        if (!slotEl) return;

        symbolsObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-symbols') {
              parseSymbolsFromElement();
              break;
            }
          }
        });
        symbolsObserver.observe(slotEl, { attributes: true, attributeFilter: ['data-symbols'] });
      } catch (e) {
        // Ignore
      }
    };

    onMounted(() => {
      parseInitialValue();
      nextTick(() => {
        syncGameFunnelSteps();
        startObservingSymbols();
      });
    });

    onBeforeUnmount(() => {
      if (symbolsObserver) {
        symbolsObserver.disconnect();
        symbolsObserver = null;
      }
    });

    // Re-read symbols when switching back to this tab
    watch(() => props.parserValues, () => {
      parseSymbolsFromElement();
      // Re-attach observer in case iframe was rebuilt
      if (symbolsObserver) symbolsObserver.disconnect();
      nextTick(() => startObservingSymbols());
    }, { immediate: false });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
      nextTick(() => syncGameFunnelSteps());
    }, { deep: true });

    return {
      t,
      combinations,
      availableSymbols,
      totalProbability,
      getSymbolDisplay,
      isImageSymbol,
      getSymbolImage,
      onCombinationChange,
      addCombination,
      removeCombination,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.game-slot-combinations-editor {
  width: 100%;

  .combinations-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .combination-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
    background: $white;
    border: 1px solid $solitude;
    border-radius: 6px;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: $brand-color-primary;
    }
  }

  .combination-item-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding-top: 1.5rem;

    .drag-handle {
      color: $mid-grey;
      cursor: grab;
      font-size: 0.875rem;

      &:active {
        cursor: grabbing;
      }
    }

    .combination-number {
      font-size: 0.75rem;
      color: $mid-grey;
      font-weight: 600;
    }
  }

  .combination-fields {
    flex: 1;
  }

  .field-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
    flex-wrap: wrap;
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
      font-size: 0.7rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    &.field-symbols {
      flex: 0 0 auto;
    }

    &.field-prize {
      flex: 2;
      min-width: 120px;
    }

    &.field-probability {
      flex: 1;
      min-width: 80px;
    }

  }

  .symbols-selector {
    display: flex;
    gap: 0.25rem;

    .symbol-dropdown {
      width: 60px;

      :deep(.p-dropdown-label) {
        padding: 0.3rem;
        text-align: center;
        font-size: 1.25rem;
        line-height: 1.4;
        overflow: hidden;
      }

      :deep(.p-dropdown-trigger) {
        width: 1.25rem;
      }
    }
  }

  .symbol-preview {
    font-size: 1.25rem;
    line-height: 1;
  }

  .symbol-preview-img {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .symbol-thumb {
    width: 28px;
    height: 28px;
    max-width: 28px;
    max-height: 28px;
    object-fit: contain;
    border-radius: 3px;
    display: block;
  }

  .symbol-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .symbol-thumb {
      width: 28px;
      height: 28px;
      max-width: 28px;
      max-height: 28px;
    }
  }

  .symbol-option-icon {
    font-size: 1.25rem;
    line-height: 1;
  }

  .probability-input {
    display: flex;
    align-items: center;
    gap: 0.25rem;

    :deep(.p-inputnumber) {
      flex: 1;
    }

    .probability-suffix {
      font-size: 0.875rem;
      color: $mid-grey;
    }
  }

  .field-actions {
    display: flex;
    align-items: flex-end;
    padding-bottom: 0.25rem;
  }

  .combinations-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    border-top: 1px solid $solitude;
  }

  .probability-total {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;

    .total-value {
      font-weight: 600;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
    }

    &.is-valid .total-value {
      background: rgba(76, 175, 80, 0.15);
      color: #2e7d32;
    }

    &.is-warning .total-value {
      background: rgba(255, 152, 0, 0.15);
      color: #e65100;
    }
  }
}
</style>

<style lang="scss">
.slot-symbol-dropdown-panel {
  .p-dropdown-item {
    padding: 0.4rem 0.75rem;
  }

  .symbol-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .symbol-thumb {
    width: 28px;
    height: 28px;
    max-width: 28px;
    max-height: 28px;
    object-fit: contain;
    border-radius: 3px;
    display: block;
  }

  .symbol-option-icon {
    font-size: 1.25rem;
    line-height: 1;
  }
}
</style>
